import { CSSInterpolation } from '@emotion/serialize';
import * as React from 'react';
import { useFocus } from '../../hooks';
import { useTheme } from '../../theme';
import { FontFamily } from '../../theme/tokens';
import { CSSRulesFunction } from '../../theme/types';
import Icon, { IconVariant } from '../Icon';
import VisuallyHidden from '../VisuallyHidden';
import { Text } from '../typography';
import { FormFieldStatus, inputReset, statusStyle } from './appearance';

interface AppearanceProps {
  status?: FormFieldStatus;
  leftAccessory?: React.ReactNode;
  rightAccessory?: React.ReactNode;
}

type HTMLAttributes = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  keyof AppearanceProps
>;

interface InteractionState {
  focused?: boolean;
  disabled?: boolean;
}

interface InternalInputProps extends AppearanceProps, InteractionState {}

const inputStyle: CSSRulesFunction<InternalInputProps> = (theme, props) => {
  const fontSize = theme.tokens.fontSizes[theme.tokens.defaultFontSize];
  const { disabled } = props;

  return [
    {
      fontFamily: theme.tokens.FontFamilies[FontFamily.UI],
      display: 'block',
      width: '100%',
      margin: 0,
      background: 'none',
      cursor: disabled ? 'not-allowed' : 'auto',
      border: 0,
      borderRadius: 0,
      padding: 0,
      appearance: 'none',
      color: 'inherit',
      '&:focus': {
        boxShadow: 'none',
        outline: 'none',
      },
      ...(disabled && {
        '&::placeholder': {
          color: statusStyle(theme, props).textColor,
        },
      }),
    },
    fontSize,
    inputReset,
  ];
};

const inputContainerStyle: CSSRulesFunction<InternalInputProps> = (
  theme,
  props
) => {
  const { status, disabled, focused } = props;

  const colors = statusStyle(theme, { status, disabled });

  const {
    baseBorderColor,
    focusBorderColor,
    outlineColor,
    textColor,
    backgroundColor,
    borderWidth = '1px',
  } = colors;

  let boxShadow = `inset 0 0 0 ${borderWidth} ${baseBorderColor}`;

  if (focused) {
    boxShadow = `inset 0 0 0 2px ${outlineColor}`;
  }

  return {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'stretch',
    backgroundColor,
    color: textColor,
    overflow: 'hidden',
    borderRadius: theme.tokens.borderScale * 1.5,
    boxShadow,
    transition: 'border-color .15s ease-in-out,box-shadow .15s ease-in-out',
    padding: theme.spacing(3),
    '&:hover': !focused && {
      boxShadow: `inset 0 0 0 ${borderWidth} ${focusBorderColor}`,
    },
  };
};

const inputAccessoryStyle: CSSInterpolation = {
  display: 'flex',
  alignItems: 'center',
};

const leftInputAccessoryStyle: CSSRulesFunction<
  Pick<InteractionState, 'disabled'>
> = (theme, { disabled }) => {
  const { textColor: color } = statusStyle(theme, { disabled });

  return [
    inputAccessoryStyle,
    { color },
    { marginRight: theme.spacing(1) },
    {
      '> svg': theme.responsiveSpacing([0, 0, 0, 0.75], ['margin']),
    },
  ];
};
const rightInputAccessoryStyle: CSSRulesFunction<
  Pick<InteractionState, 'disabled'>
> = (theme, { disabled }) => {
  const { textColor: color } = statusStyle(theme, { disabled });

  return [
    inputAccessoryStyle,
    { color },
    { marginLeft: theme.spacing(1) },
    {
      '> svg': theme.responsiveSpacing([0, 0, 0, 0.75], ['margin']),
    },
  ];
};

const inputStatusIconStyle: CSSRulesFunction<InternalInputProps> = (
  theme,
  props
) => {
  const { iconColor } = statusStyle(theme, {
    status: props.status,
    disabled: props.disabled,
  });

  if (props.disabled) {
    return { display: 'none' };
  }

  return [
    {
      display: 'flex',
      alignItems: 'center',
      paddingTop: '0',
      paddingBottom: '0',
      color: iconColor,
    },
    theme.responsiveSpacing([0, 0, 0, 0.75], ['paddingLeft', 'paddingRight']),
  ];
};

export interface InputProps extends AppearanceProps, HTMLAttributes {}

const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input(
  props,
  ref
) {
  const { theme } = useTheme();
  const { status, leftAccessory, rightAccessory, ...rest } = props;
  const { onFocus, onBlur, disabled } = props;
  const { focused, handleFocus, handleBlur } = useFocus(onFocus, onBlur);
  const styleProps = {
    status,
    leftAccessory,
    rightAccessory,
    focused,
    disabled,
  };
  const statusIconCSS = inputStatusIconStyle(theme, styleProps);

  return (
    <div css={inputContainerStyle(theme, styleProps)} data-field-control>
      {leftAccessory ? (
        <div css={leftInputAccessoryStyle(theme, { disabled })}>
          {leftAccessory}
        </div>
      ) : null}

      <input
        css={inputStyle(theme, styleProps)}
        {...rest}
        aria-required={rest.required}
        aria-invalid={status === FormFieldStatus.Error}
        onFocus={handleFocus}
        onBlur={handleBlur}
        ref={ref}
      />

      {status === FormFieldStatus.Success && (
        <div css={statusIconCSS} aria-hidden>
          <Icon size={6} name="tick" variant={IconVariant.Confirmation} />
          <VisuallyHidden>
            <Text>Success icon</Text>
          </VisuallyHidden>
        </div>
      )}

      {status === FormFieldStatus.Error && (
        <div css={statusIconCSS} aria-hidden>
          <Icon size={6} name="error" variant={IconVariant.Error} />
          <VisuallyHidden>
            <Text>Error icon</Text>
          </VisuallyHidden>
        </div>
      )}

      {rightAccessory ? (
        <div css={rightInputAccessoryStyle(theme, { disabled })}>
          {rightAccessory}
        </div>
      ) : null}
    </div>
  );
});

export default Input;
